import { ThemeKey } from "@smartsheet/design-tokens";
import { useIsComposingHandler } from "@smartsheet/lodestar-utils";
import { useEnsuredForwardedRef } from "@smartsheet/lodestar-utils";
import React, { forwardRef, ReactNode, useState } from "react";
import { Omit } from "utility-types";
import { noop } from "../../constants/noop";
import { useTheme } from "../../theme/utils/useTheme";
import { getInputFieldHeightValue, InputFieldBaseProps } from "../../util/inputFieldBaseProps";
import { useFocusMode } from "../focusMode";
import { StyledContentAfterContainer } from "./styled/StyledContentAfterContainer";
import { StyledContentBeforeContainer } from "./styled/StyledContentBeforeContainer";
import { StyledInput } from "./styled/StyledInput";
import { StyledInputContainer } from "./styled/StyledInputContainer";
import { TextInputRootBox } from "./styled/TextInputRootBox";
import { TextInputRootUnderline } from "./styled/TextInputRootUnderline";

/**
 * Used to provide the left and right customizations access to the current state of the TextInput.
 */
interface InnerState {
    isFocused: boolean;
    isHovered: boolean;
}

// Omitting "style" because, it's ambiguous as to which sub element
// the style should go on (the input itself or the wrapper).
// Omitting "defaultValue" because we're only support controlled use
// cases right now.
export interface TextInputProps
    extends InputFieldBaseProps,
        Omit<
            React.InputHTMLAttributes<HTMLInputElement>,
            "disabled" | "onChange" | "value" | "style" | "defaultValue"
        > {
    /** Handler to be called when the input changes. */
    onChange: (value: string, event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement>) => void;
    /** The value of the text-area. */
    value: string;
    /**
     * A classname to be applied to the root element of the component.
     */
    className?: string;
    /**
     * A `clientId` prop is provided for specified elements, which is a unique
     * string that appears as a data attribute `data-client-id` in the rendered code,
     * serving as a hook for automated tests */
    clientId?: string;

    /**
     * Piece of UI to show after the typeable area, normally a 20x20 icon.
     */
    contentAfter?: ReactNode | ((state: InnerState) => ReactNode);
    /**
     * Piece of UI to show before the typeable area, normally a 20x20 icon.
     */
    contentBefore?: ReactNode | ((state: InnerState) => ReactNode);
    /** Flag to determine if the input is disabled */
    isDisabled?: boolean;
    /** Type of text box. We're not simply passing it through raw since we want to disallow radio, checkbox, etc. */
    type?: "text" | "email" | "number" | "tel" | "url" | "password" | "search";
    /** If "error" then it gets a red border. */
    validationState?: "error";
    /** "box" gives a border all around, "underline" just a bottom border */
    variant?: "box" | "underline";
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>((props, parentRef) => {
    const {
        contentAfter,
        contentBefore,
        className,
        value,
        onChange,
        clientId,
        variant = "box",
        type = "text",
        onFocus,
        onBlur,
        onKeyDown,
        onKeyUp,
        validationState,
        isDisabled = false,
        inputFieldHeight: inputFieldHeightProp,
        ...htmlAttributes
    } = props;

    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const inputFieldHeight = getInputFieldHeightValue(themeMode, inputFieldHeightProp);

    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [isHovered, setIsHovered] = useState<boolean>(false);

    const textInputRef = useEnsuredForwardedRef(parentRef);

    function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
        onChange(e.target.value, e);
    }

    function handleOnFocus(e: React.FocusEvent<HTMLInputElement>) {
        setIsFocused(true);
        if (onFocus) {
            onFocus(e);
        }
    }

    function handleOnBlur(e: React.FocusEvent<HTMLInputElement>) {
        setIsFocused(false);
        if (onBlur) {
            onBlur(e);
        }
    }

    const handleKeyDown = useIsComposingHandler(onKeyDown || noop);
    const handleKeyUp = useIsComposingHandler(onKeyUp || noop);

    const OuterComponent = variant === "box" ? TextInputRootBox : TextInputRootUnderline;
    const { focusMode } = useFocusMode();

    return (
        <OuterComponent
            inputFieldHeight={inputFieldHeight}
            isDisabled={isDisabled}
            isFocused={isFocused}
            isHovered={isHovered}
            validationState={validationState}
            className={className}
            themeMode={themeMode}
            hasContentAfter={!!contentAfter}
            focusMode={focusMode}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => {
                textInputRef.current?.focus();
            }}
        >
            {contentBefore && (
                <StyledContentBeforeContainer
                    themeMode={themeMode}
                    validationState={validationState}
                    isFocused={isFocused}
                    variant={variant}
                >
                    {typeof contentBefore === "function"
                        ? contentBefore({
                              isFocused,
                              isHovered,
                          })
                        : contentBefore}
                </StyledContentBeforeContainer>
            )}
            <StyledInputContainer>
                <StyledInput
                    variant={variant}
                    themeMode={themeMode}
                    type={type}
                    value={value}
                    onChange={handleOnChange}
                    focusMode={focusMode}
                    ref={textInputRef}
                    data-client-id={clientId}
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur}
                    onKeyDown={(onKeyDown && handleKeyDown) || undefined}
                    onKeyUp={(onKeyUp && handleKeyUp) || undefined}
                    isHovered={isHovered}
                    aria-disabled={isDisabled}
                    isFocused={isFocused}
                    {...htmlAttributes}
                    readOnly={isDisabled ? true : htmlAttributes.readOnly ? htmlAttributes.readOnly : false}
                    hasContentAfter={!!contentAfter}
                    hasContentBefore={!!contentBefore}
                    validationState={validationState}
                />
            </StyledInputContainer>
            {contentAfter && (
                <StyledContentAfterContainer
                    themeMode={themeMode}
                    validationState={validationState}
                    isFocused={isFocused}
                    focusMode={focusMode}
                    variant={variant}
                >
                    {typeof contentAfter === "function"
                        ? contentAfter({
                              isFocused,
                              isHovered,
                          })
                        : contentAfter}
                </StyledContentAfterContainer>
            )}
        </OuterComponent>
    );
});

TextInput.displayName = "TextInput";
